import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const InnovacionesPage: React.FC = () => {
    const { language } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState<number>(1);

    const t = (es: string, en: string) => language === 'es' ? es : en;

    useEffect(() => {
        const sectionParam = searchParams.get('section');
        if (sectionParam) {
            const sectionNumber = parseInt(sectionParam);
            if (sectionNumber === 4) {
                // Redirigir a la página de gamificación
                navigate('/gamification', { replace: true });
            } else if (sectionNumber >= 1 && sectionNumber <= 5) {
                setActiveSection(sectionNumber);
            }
        }
    }, [searchParams, navigate]);

    const handleThemeChange = (theme: number) => {
        // Navegar a la página del libro correspondiente
        const bookRoutes = {
            2: '/libros/1',
            3: '/libros/2', 
            4: '/libros/3',
            5: '/libros/4',
            6: '/libros/5',
            7: '/libros/6',
            8: '/libros/7',
            9: '/libros/8'
        };
        const route = bookRoutes[theme as keyof typeof bookRoutes];
        if (route) {
            window.location.href = route;
        }
    };

    // Función para renderizar la sección 1: Filosofía Princonser
    const renderSection1 = () => (
            <section className="relative py-20 lg:py-32 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-10"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                            {t('Filosofía Princonser', 'Princonser Philosophy')}
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                            {t(
                                'La brújula que transforma el aprendizaje en pasión, propósito y poder personal.',
                                'The compass that transforms learning into passion, purpose, and personal power.'
                            )}
                        </p>
                    </div>

                    {/* ¿Por qué Princonser es único? */}
                    <div className="mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('¿Por qué Princonser es único?', 'Why is Princonser unique?')}
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {/* La Verdad como punto de partida */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🔍</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('La Verdad como punto de partida', 'Truth as a starting point')}
                                </h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Cada lección invita al niño a cuestionar, descubrir y validar por sí mismo, fomentando una curiosidad auténtica.',
                                        'Each lesson invites the child to question, discover, and validate for themselves, fostering authentic curiosity.'
                                    )}
                                </p>
                            </div>

                            {/* Ética en acción */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">⚖️</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Ética en acción', 'Ethics in action')}
                                </h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'No basta con saber; hay que hacer el bien. Princonser integra valores universales en cada cuento y ejercicio para construir carácter.',
                                        'Knowing is not enough; you must do good. Princonser integrates universal values in every story and exercise to build character.'
                                    )}
                                </p>
                            </div>

                            {/* Pensamiento dialéctico */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🔄</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Pensamiento dialéctico', 'Dialectical thinking')}
                                </h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Enseña a identificar lo que conviene conservar y lo que hay que transformar, desarrollando resiliencia y adaptabilidad.',
                                        'Teaches to identify what should be preserved and what needs to be transformed, developing resilience and adaptability.'
                                    )}
                                </p>
                            </div>

                            {/* Cuatro áreas, un todo */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🎯</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Cuatro áreas, un todo', 'Four areas, one whole')}
                                </h3>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Salud Mental-Emocional, Social-Profesional, Biológica-Física y Financiera: un modelo holístico que garantiza un desarrollo equilibrado.',
                                        'Mental-Emotional, Social-Professional, Biological-Physical, and Financial Health: a holistic model that ensures balanced development.'
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* El impacto Princonser en tu hijo */}
                    <div className="mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('El impacto Princonser en tu hijo', 'The Princonser impact on your child')}
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                            <div className="text-6xl mb-6">🌟</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Autonomía real', 'Real autonomy')}
                                </h3>
                            <p className="text-lg text-gray-700">
                                {t('Toma de decisiones desde los 6 años.', 'Decision-making from age 6.')}
                                </p>
                            </div>
                            
                            <div className="text-center">
                            <div className="text-6xl mb-6">🎯</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Maestría práctica', 'Practical mastery')}
                                </h3>
                            <p className="text-lg text-gray-700">
                                {t('Conocimientos que se aplican aquí y ahora.', 'Knowledge that applies here and now.')}
                                </p>
                            </div>
                            
                            <div className="text-center">
                            <div className="text-6xl mb-6">💫</div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Propósito claro', 'Clear purpose')}
                                </h3>
                            <p className="text-lg text-gray-700">
                                {t('Cada actividad conecta con un valor universal.', 'Every activity connects with a universal value.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Da el siguiente paso */}
                    <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 lg:p-12 text-center text-white">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                            {t('Da el siguiente paso', 'Take the next step')}
                        </h2>
                        <p className="text-xl mb-8 max-w-3xl mx-auto">
                            {t(
                                'Descubre cómo el Método Princonser convierte el aprendizaje en una aventura de crecimiento interior y logros tangibles.',
                                'Discover how the Princonser Method turns learning into an adventure of inner growth and tangible achievements.'
                            )}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="px-8 py-4 bg-white text-blue-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                                {t('Quiero saber más', 'I want to know more')}
                            </button>
                            <button className="px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
                                {t('Únete a la comunidad', 'Join the community')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
    );

    // Función para renderizar la sección 2: Metodología del Continuo Desarrollo
    const renderSection2 = () => (
            <section className="py-20 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                        {t('Currículum Holístico', 'Holistic Curriculum')}
                        </h2>
                        <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                            {t('Desarrollo integral en todas las dimensiones humanas', 'Integral development in all human dimensions')}
                        </p>
                    </div>

                    {/* ¿Por qué es la revolución educativa? */}
                    <div className="mb-16">
                        <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('¿Por qué es la revolución educativa?', 'Why is it the educational revolution?')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Actualización permanente */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🔄</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Actualización permanente', 'Permanent updating')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Tus hijos reciben contenidos que evolucionan al mismo ritmo que el mundo: ciencia, tecnología y valores siempre vigentes.',
                                        'Your children receive content that evolves at the same pace as the world: science, technology, and values always current.'
                                    )}
                                </p>
                            </div>

                            {/* Aprendizaje a su medida */}
                        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🎯</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Aprendizaje a su medida', 'Learning at their pace')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Sin calendarios rígidos: cada niño decide qué repasar y cuándo avanzar, construyendo autonomía y motivación real.',
                                        'No rigid schedules: each child decides what to review and when to advance, building real autonomy and motivation.'
                                    )}
                                </p>
                            </div>

                            {/* Sistema que te conoce */}
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🤖</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Sistema que te conoce', 'System that knows you')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Evaluación bidireccional: la plataforma se adapta a tus necesidades y les brinda feedback inmediato para crecer sin estancarse.',
                                        'Bidirectional evaluation: the platform adapts to your needs and provides immediate feedback to grow without stagnation.'
                                    )}
                                </p>
                            </div>

                            {/* Currículo vivo */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                            <div className="text-6xl mb-6 flex justify-center">📚</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Currículo vivo', 'Living curriculum')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Un pensum flexible, digital y uniforme, basado en el Método Princonser, que garantiza calidad y coherencia en cada lección.',
                                        'A flexible, digital, and uniform curriculum based on the Princonser Method, ensuring quality and coherence in every lesson.'
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* El impacto en tus hijos */}
                    <div className="mb-16">
                        <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('El impacto en tus hijos', 'The impact on your children')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="text-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                                <div className="text-5xl mb-4">⭐</div>
                                <h4 className="text-xl font-bold text-gray-800 mb-3">
                                    {t('Protagonistas de su propio futuro', 'Protagonists of their own future')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('Se convierten en agentes activos, eligiendo retos y celebrando cada logro.', 'They become active agents, choosing challenges and celebrating each achievement.')}
                                </p>
                            </div>
                            
                            <div className="text-center bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
                                <div className="text-5xl mb-4">🚀</div>
                                <h4 className="text-xl font-bold text-gray-800 mb-3">
                                    {t('Conocimiento siempre relevante', 'Always relevant knowledge')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('Prepárales para los desafíos de hoy… y los de mañana.', 'Prepare them for today\'s challenges... and tomorrow\'s.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Da el salto al aprendizaje sin límites */}
                    <div className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 rounded-3xl p-8 lg:p-12 text-center text-white">
                        <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                            {t('Da el salto al aprendizaje sin límites', 'Take the leap to unlimited learning')}
                        </h3>
                        <p className="text-xl mb-8 max-w-3xl mx-auto">
                            {t(
                                'Descubre cómo la Metodología del Continuo Desarrollo convierte cada lección en una aventura dinámica y personalizada.',
                                'Discover how the Continuous Development Methodology turns every lesson into a dynamic and personalized adventure.'
                            )}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="px-8 py-4 bg-white text-green-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                                {t('Quiero saber más', 'I want to know more')}
                            </button>
                            <button className="px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:scale-105">
                                {t('Únete a nuestra comunidad', 'Join our community')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
    );

    // Función para renderizar la sección 3: Sistema de Compensación Transnivel
    const renderSection3 = () => (
            <section className="py-20 lg:py-32 bg-gradient-to-br from-yellow-50 to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                        {t('Método de Enseñanza', 'Teaching Method')}
                        </h2>
                        <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                            {t('Aprende de manera efectiva y significativa', 'Learn effectively and meaningfully')}
                        </p>
                    </div>

                    {/* ¿Por qué es disruptivo? */}
                    <div className="mb-16">
                        <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('¿Por qué es disruptivo?', 'Why is it disruptive?')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {/* Promueve el desarrollo integral */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🌱</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Promueve el desarrollo integral', 'Promotes integral development')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Desarrolla el crecimiento integral de tu hijo en todas las dimensiones humanas: espiritual, social y biológica',
                                        'Develops the integral growth of your child in all human dimensions: spiritual, social, and biological'
                                    )}
                                </p>
                            </div>

                            {/* Reconocimiento y meritocracia */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-green-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🎖️</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Reconocimiento y meritocracia', 'Recognition and meritocracy')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Con el uso de herramientas lúdicas, aumenta su confianza con reconocimientos personales, a través de títulos e insignias',
                                        'With the use of playful tools, increases their confidence with personal recognitions through titles and badges'
                                    )}
                                </p>
                            </div>

                            {/* Crecimiento colaborativo */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-purple-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🤝</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Crecimiento colaborativo', 'Collaborative growth')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Tu hijo es acompañado en cada paso, invitado a formar parte en actividades y eventos internacionales, superando rankings y alcanzando metas globales',
                                        'Your child is accompanied every step of the way, invited to participate in international activities and events, surpassing rankings and reaching global goals'
                                    )}
                                </p>
                            </div>

                            {/* Guía e iniciación */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-orange-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🚀</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Guía e iniciación', 'Guidance and initiation')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'No nos quedamos en la teoría, presentamos oportunidades, productos y actividades para poner en práctica sus conocimientos.',
                                        'We don\'t stay in theory, we present opportunities, products, and activities to put their knowledge into practice.'
                                    )}
                                </p>
                            </div>

                            {/* Recompensas económicas */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-red-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">💰</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Recompensas económicas', 'Economic rewards')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Cada actividad otorga créditos que generan beneficios a corto y largo plazo',
                                        'Each activity grants credits that generate short and long-term benefits'
                                    )}
                                </p>
                            </div>

                            {/* Incubadora de negocios */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-teal-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🏢</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                    {t('Incubadora de negocios', 'Business incubator')}
                                </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                    {t(
                                        'Una parte de las ganancias nutre proyectos creativos de niños, convirtiendo tus logros en oportunidades reales para otros.',
                                        'A portion of the earnings nurtures children\'s creative projects, turning your achievements into real opportunities for others.'
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* El motor de tu motivación */}
                    <div className="mb-16">
                        <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            {t('El motor de tu motivación', 'The engine of your motivation')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                                <div className="text-5xl mb-4">🎯</div>
                                <h4 className="text-xl font-bold text-gray-800 mb-3">
                                    {t('Autonomía', 'Autonomy')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('Decide en qué retos invertir tu tiempo y recibe recompensa directa.', 'Decide which challenges to invest your time in and receive direct reward.')}
                                </p>
                            </div>
                            
                            <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                                <div className="text-5xl mb-4">⭐</div>
                                <h4 className="text-xl font-bold text-gray-800 mb-3">
                                    {t('Maestría', 'Mastery')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('Sube de nivel demostrando tus habilidades y duplica tus beneficios.', 'Level up by demonstrating your skills and double your benefits.')}
                                </p>
                            </div>
                            
                            <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                                <div className="text-5xl mb-4">💫</div>
                                <h4 className="text-xl font-bold text-gray-800 mb-3">
                                    {t('Propósito', 'Purpose')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('Contribuye el impulso de ideas reales de tus compañeros.', 'Contribute to the momentum of real ideas from your peers.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Súmate hoy */}
                    <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-3xl p-8 lg:p-12 text-center text-white">
                        <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                            {t('Súmate hoy', 'Join today')}
                        </h3>
                        <p className="text-xl mb-8 max-w-3xl mx-auto">
                            {t(
                                'Conviértete en protagonista de una comunidad donde cada paso de aprendizaje se transforma en valor compartido.',
                                'Become a protagonist of a community where every step of learning transforms into shared value.'
                            )}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="px-8 py-4 bg-white text-orange-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                                {t('Quiero unirme', 'I want to join')}
                            </button>
                            <button className="px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-orange-600 transition-all duration-300 transform hover:scale-105">
                                {t('Descubre más', 'Discover more')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
    );

    // Función para renderizar la sección 4: Gamificación
    const renderSection4 = () => (
        <section className="py-20 lg:py-32 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                        {t('Gamificación', 'Gamification')}
                    </h2>
                    <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        {t('Aprende jugando, crece ganando', 'Learn by playing, grow by winning')}
                    </p>
                </div>

                {/* ¿Por qué la gamificación transforma el aprendizaje? */}
                <div className="mb-16">
                    <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                        {t('¿Por qué la gamificación transforma el aprendizaje?', 'Why does gamification transform learning?')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Sistema de niveles */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-purple-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">📈</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Sistema de niveles', 'Level system')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Progreso visual y tangible que motiva a los niños a superarse constantemente, creando una sensación de logro continuo.',
                                    'Visual and tangible progress that motivates children to constantly improve, creating a sense of continuous achievement.'
                                )}
                            </p>
                        </div>

                        {/* Insignias y logros */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-pink-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🏆</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Insignias y logros', 'Badges and achievements')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Reconocimiento inmediato de habilidades y competencias, fomentando la autoestima y la confianza en sí mismos.',
                                    'Immediate recognition of skills and competencies, fostering self-esteem and self-confidence.'
                                )}
                            </p>
                        </div>

                        {/* Misiones y retos */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-indigo-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🎯</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Misiones y retos', 'Missions and challenges')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Actividades estructuradas que convierten el aprendizaje en una aventura emocionante y desafiante.',
                                    'Structured activities that turn learning into an exciting and challenging adventure.'
                                )}
                            </p>
                        </div>

                        {/* Competencia sana */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🤝</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Competencia sana', 'Healthy competition')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Fomenta la colaboración y el respeto mutuo mientras desarrolla habilidades de liderazgo y trabajo en equipo.',
                                    'Fosters collaboration and mutual respect while developing leadership and teamwork skills.'
                                )}
                            </p>
                        </div>

                        {/* Recompensas instantáneas */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-green-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">⭐</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Recompensas instantáneas', 'Instant rewards')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Feedback inmediato que refuerza el comportamiento positivo y motiva a continuar aprendiendo.',
                                    'Immediate feedback that reinforces positive behavior and motivates continued learning.'
                                )}
                            </p>
                        </div>

                        {/* Personalización */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-yellow-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🎨</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Personalización', 'Personalization')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Experiencia adaptada al ritmo y estilo de aprendizaje de cada niño, maximizando su potencial.',
                                    'Experience adapted to each child\'s learning pace and style, maximizing their potential.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Beneficios de la gamificación */}
                <div className="mb-16">
                    <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                        {t('Beneficios de la gamificación', 'Benefits of gamification')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">🧠</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Motivación intrínseca', 'Intrinsic motivation')}
                            </h4>
                            <p className="text-gray-600">
                                {t('Los niños aprenden por el placer de descubrir y superarse.', 'Children learn for the pleasure of discovering and improving themselves.')}
                            </p>
                        </div>
                        
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">💪</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Perseverancia', 'Perseverance')}
                            </h4>
                            <p className="text-gray-600">
                                {t('Desarrollan resiliencia ante los desafíos y aprenden a no rendirse.', 'They develop resilience in the face of challenges and learn not to give up.')}
                            </p>
                        </div>
                        
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">🎉</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Aprendizaje divertido', 'Fun learning')}
                            </h4>
                            <p className="text-gray-600">
                                {t('El conocimiento se adquiere de manera natural y entretenida.', 'Knowledge is acquired naturally and entertainingly.')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* ¡Comienza tu aventura! */}
                <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-3xl p-8 lg:p-12 text-center text-white">
                    <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                        {t('¡Comienza tu aventura!', 'Start your adventure!')}
                    </h3>
                    <p className="text-xl mb-8 max-w-3xl mx-auto">
                        {t(
                            'Únete a una comunidad donde el aprendizaje se convierte en el juego más emocionante de todos.',
                            'Join a community where learning becomes the most exciting game of all.'
                        )}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="px-8 py-4 bg-white text-purple-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                            {t('Jugar ahora', 'Play now')}
                        </button>
                        <button className="px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-purple-600 transition-all duration-300 transform hover:scale-105">
                            {t('Ver demo', 'Watch demo')}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );

    // Función para renderizar la sección 5: Ecosistema Incentivos
    const renderSection5 = () => (
        <section className="py-20 lg:py-32 bg-gradient-to-br from-green-50 to-teal-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                        {t('Ecosistema Incentivos', 'Incentive Ecosystem')}
                    </h2>
                    <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        {t('Donde cada logro genera valor para todos', 'Where every achievement generates value for everyone')}
                    </p>
                </div>

                {/* ¿Cómo funciona el ecosistema? */}
                <div className="mb-16">
                    <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                        {t('¿Cómo funciona el ecosistema?', 'How does the ecosystem work?')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Créditos educativos */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-green-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">💎</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Créditos educativos', 'Educational credits')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Cada actividad completada genera créditos que pueden ser intercambiados por beneficios reales y oportunidades de aprendizaje.',
                                    'Each completed activity generates credits that can be exchanged for real benefits and learning opportunities.'
                                )}
                            </p>
                        </div>

                        {/* Marketplace de conocimientos */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-teal-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🛒</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Marketplace de conocimientos', 'Knowledge marketplace')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Los niños pueden intercambiar sus conocimientos y habilidades con otros, creando una economía del saber.',
                                    'Children can exchange their knowledge and skills with others, creating an economy of knowledge.'
                                )}
                            </p>
                        </div>

                        {/* Fondos comunitarios */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🏦</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Fondos comunitarios', 'Community funds')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Una parte de los beneficios se reinvierte en proyectos educativos y creativos de la comunidad.',
                                    'A portion of the benefits is reinvested in educational and creative community projects.'
                                )}
                            </p>
                        </div>

                        {/* Mentoría entre pares */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-purple-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">👥</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Mentoría entre pares', 'Peer mentoring')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Los niños más avanzados pueden convertirse en mentores de otros, ganando reconocimiento y beneficios adicionales.',
                                    'More advanced children can become mentors to others, earning recognition and additional benefits.'
                                )}
                            </p>
                        </div>

                        {/* Eventos internacionales */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-orange-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🌍</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Eventos internacionales', 'International events')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Acceso a competencias, conferencias y encuentros globales que expanden horizontes y crean conexiones.',
                                    'Access to competitions, conferences, and global meetings that expand horizons and create connections.'
                                )}
                            </p>
                        </div>

                        {/* Incubadora de proyectos */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-red-500 text-center">
                            <div className="text-6xl mb-6 flex justify-center">🚀</div>
                            <h4 className="text-2xl font-bold text-gray-800 mb-4">
                                {t('Incubadora de proyectos', 'Project incubator')}
                            </h4>
                            <p className="text-lg text-gray-700 leading-relaxed">
                                {t(
                                    'Los mejores proyectos de los niños reciben apoyo para convertirse en realidades tangibles.',
                                    'The best children\'s projects receive support to become tangible realities.'
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Impacto en la comunidad */}
                <div className="mb-16">
                    <h3 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                        {t('Impacto en la comunidad', 'Community impact')}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">🤝</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Colaboración', 'Collaboration')}
                            </h4>
                            <p className="text-gray-600">
                                {t('Los niños aprenden a trabajar juntos y apoyarse mutuamente.', 'Children learn to work together and support each other.')}
                            </p>
                        </div>
                        
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">💡</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Innovación', 'Innovation')}
                            </h4>
                            <p className="text-gray-600">
                                {t('Se fomenta la creatividad y el pensamiento innovador.', 'Creativity and innovative thinking are encouraged.')}
                            </p>
                        </div>
                        
                        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
                            <div className="text-5xl mb-4">🌟</div>
                            <h4 className="text-xl font-bold text-gray-800 mb-3">
                                {t('Liderazgo', 'Leadership')}
                            </h4>
                            <p className="text-gray-600">
                                {t('Los niños desarrollan habilidades de liderazgo y responsabilidad social.', 'Children develop leadership skills and social responsibility.')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Únete al ecosistema */}
                <div className="bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 rounded-3xl p-8 lg:p-12 text-center text-white">
                    <h3 className="text-3xl sm:text-4xl font-bold mb-6">
                        {t('Únete al ecosistema', 'Join the ecosystem')}
                    </h3>
                    <p className="text-xl mb-8 max-w-3xl mx-auto">
                        {t(
                            'Forma parte de una comunidad donde cada logro individual contribuye al crecimiento colectivo.',
                            'Be part of a community where every individual achievement contributes to collective growth.'
                        )}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="px-8 py-4 bg-white text-green-600 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
                            {t('Participar ahora', 'Participate now')}
                        </button>
                        <button className="px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:scale-105">
                            {t('Conocer más', 'Learn more')}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Navbar */}
            <Navbar currentTheme={1} onThemeChange={handleThemeChange} />
            {/* Contenido principal con padding-top para el navbar fijo */}
            <main className="pt-16">
                {/* Renderizar la sección activa según el parámetro de URL */}
                {activeSection === 1 && renderSection1()}
                {activeSection === 2 && renderSection2()}
                {activeSection === 3 && renderSection3()}
                {activeSection === 4 && renderSection4()}
                {activeSection === 5 && renderSection5()}
            </main>
            {/* Footer */}
            <Footer />
        </div>
    );
};

export default InnovacionesPage; 